import { useEffect } from 'react';
import slugify from 'slugify';
import tippy, { hideAll } from 'tippy.js';
import 'tippy.js/animations/scale-subtle.css';
import 'tippy.js/dist/tippy.css';

const GLOSSARY_OPEN_CLASS = 'glossary-is-open';

const generateToolTip =({ term, definition, isLight }) => {
  return `
    <div class="tooltip infoPopup infoPopup--glossary ${isLight ? 'tooltip--light' : ''}">
      <p tabIndex="0">${definition}</p>
      <a
        rel="noopener noreferrer"
        target="_blank"
        href="/interactives/${document.querySelector('#root').getAttribute('data-slug')}/app/glossary?return=${encodeURIComponent(window.location.pathname)}#${slugify(term.toLowerCase(), '_')}"
        class="button button--external button--light"
      >
        <span>Glossary</span>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M5.36764 1.97306L12.7404 0.0339112C13.481 -0.160883 14.1567 0.515015 13.9618 1.25559L12.0214 8.62681C11.8727 9.19149 11.0419 9.08688 11.0378 8.50298L11.0134 4.96817C11.0123 4.79537 10.9892 4.62777 10.9468 4.46803L1.70712 13.7081C1.31661 14.0987 0.683442 14.0987 0.292909 13.7082C-0.097624 13.3176 -0.0976381 12.6845 0.292878 12.2939L9.53557 3.05084C9.37357 3.00715 9.20341 2.98329 9.02787 2.98203L5.49123 2.9566C4.90736 2.95241 4.80296 2.12158 5.36764 1.97306Z"
            fill="#004899"
          ></path>
        </svg>
      </a>
    </div>
  `;
};

const useGlossary = (ref, isLight = false, isActive = true) => {
  useEffect(() => {
    if (!ref.current || !isActive) return;

    const textArea = ref.current;

    const existingWrappers = textArea.querySelectorAll('.glossary-wrapper');
    if (existingWrappers.length === 0) {
      const wordRefs = [...textArea.querySelectorAll('.wordDefine')];

      wordRefs.forEach((elem) => {
        const wrapper = document.createElement('span');
        wrapper.className = 'glossary-wrapper';
        wrapper.setAttribute('style', 'display: inline-block; position: relative;');
        elem.parentNode.insertBefore(wrapper, elem);
        elem.setAttribute('style', 'display: inline-block; position: relative;');
        wrapper.appendChild(elem);

        const innerText = elem.innerHTML;
        if (innerText.endsWith(' ')) {
          const space = document.createElement('span');
          space.innerHTML = '&nbsp;';
          elem.parentNode.insertBefore(space, elem.nextSibling);
          elem.innerHTML = innerText.trim();
        }
      });
    }


    const instances = tippy(textArea.querySelectorAll('.wordDefine'), {
      interactive: true,
      allowHTML: true,
      arrow: false,
      placement: 'bottom',
      animation: 'scale-subtle',
      theme: 'glossary',
      appendTo: () => document.body,
      zIndex: 9999,
      plugins: [
        {
          name: 'hideOnEscKeydown',
          defaultValue: true,
          fn({ hide }) {
            const onKeyDown = (event) => {
              if (event.keyCode === 27) {
                hide();
              }
            };

            return {
              onShow() {
                document.addEventListener('keydown', onKeyDown);
              },
              onHide() {
                document.removeEventListener('keydown', onKeyDown);
              },
            };
          },
        },
        {
          name: 'hideOnPopperBlur',
          defaultValue: true,
          fn(instance) {
            return {
              onCreate() {
                instance.popper.addEventListener('focusout', (event) => {
                  if (instance.props.hideOnPopperBlur && !instance.popper.contains(event.relatedTarget)) {
                    instance.hide();
                  }
                });
              },
            };
          },
        },
      ],
      onShow(instance) {
        hideAll({ exclude: instance });
        document.body.classList.add(GLOSSARY_OPEN_CLASS);
        instance.setContent(
          generateToolTip({
            // data-term holds the glossary title, which differs from the text when matched by a synonym.
            term: instance.reference.getAttribute('data-term') || instance.reference.innerHTML,
            definition: instance.reference.getAttribute('data-def'),
            isLight,
          })
        );
      },
      onHide() {
        document.body.classList.remove(GLOSSARY_OPEN_CLASS);
      }
    });

    return () => {
      if (Array.isArray(instances)) {
        instances.forEach(instance => instance.destroy());
      }
      document.body.classList.remove(GLOSSARY_OPEN_CLASS);
    };
    // isActive must stay a dependency: inactive slides render `inert`, and a
    // tippy instance whose hover listener is bound while its element is inert
    // never fires again even after inert is removed, so tippy has to be
    // recreated fresh each time a slide becomes active.
  }, [ref, isLight, isActive]);
};

export default useGlossary;